import React, { lazy } from 'react'
import { IonReactRouter } from '@ionic/react-router'
import { Route, Redirect, Switch } from 'react-router-dom'

import { UserRoles } from 'types/enum/enum'
import { IonContent, IonPage, IonRouterOutlet } from '@ionic/react'
import { DashboardView } from 'components/DashboardView/DashboardView'
import { DashboardLayout } from 'components/Layouts/DashboardLayout/DashboardLayout'
import ProtectedRoute from 'components/ProtectedRoute/ProtectedRoute'
import { TestProvider } from 'services/TestContext'
import { SuspenseWrapper } from './SuspensWrapper'

const LoginPage = lazy(() =>
  import('pages/login').then((module) => ({
    default: module.LoginPage
  }))
)

const CreateTest = lazy(() =>
  import('pages/create-test').then((module) => ({
    default: module.CreateTest
  }))
)

const TestsHistoryPage = lazy(() =>
  import('pages/test/history').then((module) => ({
    default: module.TestsHistoryPage
  }))
)

const TestPage = lazy(() =>
  import('pages/test').then((module) => ({
    default: module.TestPage
  }))
)

const MistakesPage = lazy(() =>
  import('pages/mistakes/MistakesPage').then((module) => ({
    default: module.MistakesPage
  }))
)

const MarkedQuestionsPage = lazy(() =>
  import('pages/marked-questions/MarkedQuestionsPage').then((module) => ({
    default: module.MarkedQuestionsPage
  }))
)

const SystemReviewsPage = lazy(() =>
  import('pages/system-reviews/SystemReviewsPage').then((module) => ({
    default: module.SystemReviewsPage
  }))
)

const AccountSettingsPage = lazy(() =>
  import('pages/account-settings/AccountSettingsPage').then((module) => ({
    default: module.AccountSettingsPage
  }))
)

const CreateResidencePage = lazy(() =>
  import('pages/create-residence/CreateResidencePage').then((module) => ({
    default: module.CreateResidencePage
  }))
)

const ResidenceTablePage = lazy(() =>
  import('pages/residence-table/ResidenceTablePage').then((module) => ({
    default: module.ResidenceTablePage
  }))
)

const AssetsPage = lazy(() =>
  import('pages/assets').then((module) => ({ default: module.AssetsPage }))
)

const SingleAssetPage = lazy(() =>
  import('pages/assets/SingleAsset').then((module) => ({
    default: module.SingleAssetPage
  }))
)

const ExamsPage = lazy(() =>
  import('pages/exams').then((module) => ({
    default: module.ExamsPage
  }))
)

const CreateExamPage = lazy(() =>
  import('pages/exams/CreateExamPage').then((module) => ({
    default: module.CreateExamPage
  }))
)

const SingleExamPage = lazy(() =>
  import('pages/exams/SingleExamPage').then((module) => ({
    default: module.SingleExamPage
  }))
)

const ReportsPage = lazy(() =>
  import('pages/reports').then((module) => ({
    default: module.ReportsPage
  }))
)

const SingleResidentReportPage = lazy(() =>
  import('pages/reports/SingleResidentReportPage').then((module) => ({
    default: module.SingleResidentReportPage
  }))
)

const QuestionsBankPage = lazy(() =>
  import('pages/questions-bank/QuestionsBankPage').then((module) => ({
    default: module.QuestionsBankPage
  }))
)

const CreateQuestionBankPage = lazy(() =>
  import('pages/questions-bank/CreateQuestionBankPage').then((module) => ({
    default: module.CreateQuestionBankPage
  }))
)

const SingleQuestionBankPage = lazy(() =>
  import('pages/questions-bank/SingleQuestionBankPage').then((module) => ({
    default: module.SingleQuestionBankPage
  }))
)
const SingleQBankDetails = lazy(() =>
  import('pages/questions-bank/SingleQuestionBankPage/SingleQBankDetails').then(
    (module) => ({
      default: module.SingleQBankDetails
    })
  )
)
const SingleQBankAssets = lazy(() =>
  import('pages/questions-bank/SingleQuestionBankPage/SingleQBankAssets').then(
    (module) => ({
      default: module.SingleQBankAssets
    })
  )
)
const SingleQBankQuestions = lazy(() =>
  import(
    'pages/questions-bank/SingleQuestionBankPage/SingleQBankQuestions'
  ).then((module) => ({
    default: module.SingleQBankQuestions
  }))
)

const EnrollmentsPage = lazy(() =>
  import('pages/enrollments/EnrollmentsPage').then((module) => ({
    default: module.EnrollmentsPage
  }))
)

const HomePage = lazy(() =>
  import('pages/home/HomePage').then((module) => ({
    default: module.HomePage
  }))
)

export const NewRouter: React.FC = () => {
  return (
    <IonReactRouter>
      <IonRouterOutlet animated={false}>
        {/* Dashboard Routes with Layout */}
        <Route path="/dashboard">
          <DashboardLayout>
            <SuspenseWrapper>
              <IonRouterOutlet animated={false}>
                <Switch>
                  <Route exact path="/dashboard">
                    <ProtectedRoute
                      allowedRoles={[UserRoles.RESIDENT, UserRoles.DIRECTOR]}
                    >
                      <DashboardView />
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/home">
                    <ProtectedRoute
                      allowedRoles={[UserRoles.RESIDENT, UserRoles.DIRECTOR]}
                    >
                      <HomePage />
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/create-test">
                    <ProtectedRoute allowedRoles={[UserRoles.RESIDENT]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <CreateTest />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/tests-history">
                    <ProtectedRoute allowedRoles={[UserRoles.RESIDENT]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <TestsHistoryPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/mistakes">
                    <ProtectedRoute allowedRoles={[UserRoles.RESIDENT]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <MistakesPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/marked-questions">
                    <ProtectedRoute allowedRoles={[UserRoles.RESIDENT]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <MarkedQuestionsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/system-reviews">
                    <ProtectedRoute allowedRoles={[UserRoles.RESIDENT]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SystemReviewsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/account-settings">
                    <ProtectedRoute
                      allowedRoles={[UserRoles.RESIDENT, UserRoles.DIRECTOR]}
                    >
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <AccountSettingsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/create-residence">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <CreateResidencePage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/residence-table">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <ResidenceTablePage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/assets">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <AssetsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/assets/:id">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleAssetPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route
                    exact
                    path="/dashboard/exams"
                    render={(props) => (
                      <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                        <IonPage>
                          <IonContent style={{ '--background': 'none' }}>
                            <ExamsPage {...props} />
                          </IonContent>
                        </IonPage>
                      </ProtectedRoute>
                    )}
                  />
                  <Route exact path="/dashboard/exams/:id">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleExamPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/create-exam">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <CreateExamPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/reports">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <ReportsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/reports/:id">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleResidentReportPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/questions-bank">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <QuestionsBankPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route
                    exact
                    path="/dashboard/questions-bank/create-question-bank"
                  >
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <CreateQuestionBankPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/questions-bank/:id">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleQuestionBankPage>
                            <SingleQBankDetails />
                          </SingleQuestionBankPage>
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/questions-bank/:id/assets">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleQuestionBankPage>
                            <SingleQBankAssets />
                          </SingleQuestionBankPage>
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/questions-bank/:id/questions">
                    <ProtectedRoute allowedRoles={[UserRoles.DIRECTOR]}>
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <SingleQuestionBankPage>
                            <SingleQBankQuestions />
                          </SingleQuestionBankPage>
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/test/:id">
                    <ProtectedRoute
                      allowedRoles={[UserRoles.RESIDENT, UserRoles.DIRECTOR]}
                    >
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <TestProvider>
                            <TestPage />
                          </TestProvider>
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route exact path="/dashboard/enrollments">
                    <ProtectedRoute
                      allowedRoles={[UserRoles.RESIDENT, UserRoles.DIRECTOR]}
                    >
                      <IonPage>
                        <IonContent style={{ '--background': 'none' }}>
                          <EnrollmentsPage />
                        </IonContent>
                      </IonPage>
                    </ProtectedRoute>
                  </Route>
                  <Route
                    component={() => (
                      <ProtectedRoute
                        allowedRoles={[UserRoles.DIRECTOR, UserRoles.RESIDENT]}
                      >
                        <Redirect to={'/dashboard'} />
                      </ProtectedRoute>
                    )}
                  />
                </Switch>
              </IonRouterOutlet>
            </SuspenseWrapper>
          </DashboardLayout>
        </Route>

        <Route
          exact
          path="/login"
          component={() => (
            <SuspenseWrapper>
              <IonPage>
                <IonContent style={{ '--background': 'none' }}>
                  <LoginPage />
                </IonContent>
              </IonPage>
            </SuspenseWrapper>
          )}
        />

        <Route
          exact
          path="/"
          component={() => (
            <ProtectedRoute
              allowedRoles={[UserRoles.DIRECTOR, UserRoles.RESIDENT]}
            >
              <Redirect to={'/dashboard'} />
            </ProtectedRoute>
          )}
        />
        <Route
          component={() => (
            <ProtectedRoute
              allowedRoles={[UserRoles.DIRECTOR, UserRoles.RESIDENT]}
            >
              <Redirect to={'/dashboard'} />
            </ProtectedRoute>
          )}
        />
      </IonRouterOutlet>
    </IonReactRouter>
  )
}
